import { useState, useRef, useEffect } from 'react';
import './CameraUpload.css';

interface CameraUploadProps {
    onImageCaptured: (imageData: string) => Promise<{ analysis: string; imageUrl: string }>;
}

export function CameraUpload({ onImageCaptured }: CameraUploadProps) {
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [result, setResult] = useState<{ analysis: string; imageUrl: string } | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    console.log('[CameraUpload] Component mounted/rendered');

    // Start camera on mount
    useEffect(() => {
        console.log('[CameraUpload] Starting camera...');
        startCamera();

        // Cleanup: stop camera on unmount
        return () => {
            console.log('[CameraUpload] Cleaning up camera stream');
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount/unmount

    const startCamera = async () => {
        console.log('[CameraUpload] Requesting camera access...');
        try {
            // Try rear camera first (environment), fallback to front (user)
            let mediaStream: MediaStream;
            try {
                console.log('[CameraUpload] Trying rear camera (environment)...');
                mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' } // Rear camera
                });
                console.log('[CameraUpload] Rear camera access granted');
            } catch (err) {
                console.warn('[CameraUpload] Rear camera not available, trying front camera', err);
                mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user' } // Front camera fallback
                });
                console.log('[CameraUpload] Front camera access granted');
            }

            setStream(mediaStream);
            console.log('[CameraUpload] Media stream set');

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                console.log('[CameraUpload] Video element connected to stream');
            }
        } catch (err) {
            console.error('[CameraUpload] Camera access error:', err);
            setError('Kamera-Zugriff verweigert oder nicht verfügbar');
        }
    };

    const capturePhoto = async () => {
        if (!videoRef.current || !canvasRef.current) {
            return;
        }

        setIsCapturing(true);

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) {
            setError('Canvas nicht verfügbar');
            setIsCapturing(false);
            return;
        }

        // Set canvas size to video size
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw current video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to base64 image
        const imageData = canvas.toDataURL('image/jpeg', 0.8);

        // Stop camera
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }

        // Send image to parent
        try {
            const analysisResult = await onImageCaptured(imageData);
            setResult(analysisResult);
            setIsCapturing(false);
        } catch (err) {
            console.error('Failed to process image:', err);
            setError('Bildverarbeitung fehlgeschlagen');
            setIsCapturing(false);
        }
    };

    // Show result if available
    if (result) {
        return (
            <div className="camera-upload">
                <div className="camera-result">
                    <img
                        src={result.imageUrl}
                        alt="Analyzed"
                        style={{
                            width: '100%',
                            maxWidth: '400px',
                            borderRadius: '8px',
                            marginBottom: '1rem'
                        }}
                    />
                    <p style={{ margin: 0, lineHeight: '1.6' }}>{result.analysis}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="camera-upload-error">
                <p>{error}</p>
                <button onClick={startCamera}>Erneut versuchen</button>
            </div>
        );
    }

    return (
        <div className="camera-upload">
            <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-preview"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <div className="camera-controls">
                <button
                    onClick={capturePhoto}
                    disabled={isCapturing || !stream}
                    className="capture-button"
                >
                    {isCapturing ? 'Verarbeite...' : 'Foto aufnehmen'}
                </button>
            </div>
        </div>
    );
}
